<template>
  <BaseDialog
    v-model="dialogOpen"
    title="Upload Screenshot"
    persistent
    save-text="Upload"
    :save-loading="uploading"
    :save-disabled="!selectedFile"
    cancel-text="Cancel"
    @save="upload"
  >
    <!-- Review reference -->
    <v-alert v-if="review" variant="tonal" type="info" density="compact" class="mb-4">
      <div class="text-body-2">{{ review.text }}</div>
      <div class="text-caption mt-1">
        {{ review.rating }}
        <v-icon icon="mdi-star" color="orange" size="x-small" />
      </div>
    </v-alert>

    <!-- Assignment entry info -->
    <div v-if="entry" class="d-flex ga-2 mb-4 flex-wrap">
      <v-chip size="small" label>
        <div :style="{ background: getPlatformGradient(entry.platform), width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px' }"></div>
        {{ entry.platform }}
      </v-chip>
      <v-chip size="small" label variant="outlined">{{ entry.location }}</v-chip>
      <v-chip size="small" label variant="outlined">{{ entry.brand }}</v-chip>
    </div>

    <!-- File input -->
    <v-file-input
      v-model="selectedFile"
      label="Photo or screenshot"
      accept="image/*"
      density="comfortable"
      variant="outlined"
      prepend-icon="mdi-image"
      :rules="[v => !!v || 'Photo is required']"
      show-size
    />

    <!-- Image preview -->
    <div v-if="previewUrl" class="mt-2 mb-4 text-center">
      <v-img
        :src="previewUrl"
        max-height="300"
        contain
        class="rounded border"
      />
    </div>

    <!-- Notes -->
    <v-textarea
      v-model="notes"
      label="Notes (optional)"
      density="comfortable"
      variant="outlined"
      hide-details
      rows="2"
      auto-grow
    />

    <v-alert v-if="error" type="error" variant="tonal" density="comfortable" class="mt-4">
      {{ error }}
    </v-alert>
  </BaseDialog>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import BaseDialog from "@/components/BaseDialog.vue";
import { useReviewSchedule } from "@/composables/useReviewSchedule.js";
import { usePlatformColors } from "@/composables/usePlatformColors.js";

const { getPlatformGradient } = usePlatformColors();

const props = defineProps({
  modelValue: Boolean,
  entry: Object,   // { assignmentId, platform, location, brand }
  review: Object,
});

const emit = defineEmits(["update:modelValue", "completed"]);

const { uploadCompletion } = useReviewSchedule();

const dialogOpen = ref(false);
const selectedFile = ref(null);
const notes = ref("");
const uploading = ref(false);
const error = ref(null);

// Preview URL for the selected file
const previewUrl = computed(() => {
  if (!selectedFile.value) return null;
  return URL.createObjectURL(selectedFile.value);
});

// Sync modelValue to internal dialogOpen
watch(() => props.modelValue, (val) => {
  dialogOpen.value = val;
  if (val) {
    selectedFile.value = null;
    notes.value = "";
    error.value = null;
  }
});

watch(dialogOpen, (val) => {
  emit("update:modelValue", val);
});

/**
 * Upload the screenshot file. Two steps:
 * 1. Upload image to assets service (R2) → get asset ID
 * 2. Complete the assignment with the asset ID
 */
async function upload() {
  if (!selectedFile.value || !props.entry?.assignmentId) return;

  uploading.value = true;
  error.value = null;

  try {
    await uploadCompletion(
      props.entry.assignmentId,
      selectedFile.value,
      props.review?.id || null,
      notes.value || null
    );
    emit("completed");
    dialogOpen.value = false;
  } catch (err) {
    error.value = err.response?.data?.error || "Upload failed";
  } finally {
    uploading.value = false;
  }
}
</script>
